import { IApiProperty, IRawUser } from "@propely/propely-typings";
import { queryClient } from "@src/index";
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from "firebase/firestore";
import { useQuery } from "react-query";
import { useAllPropertiesKey } from "../property/property.service";
import { useUserByIdKey } from "../user/user.service";
import { IRawDataType, TRawDataType } from "./dataTypes.types";

export const usePropertyDataTypesKey = (propertyID: string) => ["propertyDataTypes", { propertyID }];
export const usePropertyDataTypeKey = (propertyID: string, dataTypeID: string) => ["propertyDataType", { propertyID, dataTypeID }];
export const useGlobalDataTypesKey = () => ["dataTypes"];
export const useGlobalDataTypeKey = (id: string) => ["dataType", { dataTypeID: id }];

const fetchPropertyDataType = async (propertyID: string, dataTypeID: string) => {
  const dataType = await getDoc(doc(getFirestore(), "properties", propertyID, "dataTypes", dataTypeID));
  return TRawDataType.parse({
    id: dataType.id,
    ...dataType.data(),
  });
};

export const usePropertyDataType = (propertyID: string, dataTypeID: string) => {
  const $dataType = useQuery(usePropertyDataTypeKey(propertyID, dataTypeID), () => fetchPropertyDataType(propertyID, dataTypeID));
  return {
    $dataType,
    dataType: $dataType.data,
  };
};

const fetchAllPropertyDataTypes = async (propertyID: string) => {
  const dataTypes = await getDocs(collection(getFirestore(), "properties", propertyID, "dataTypes"));
  const result: IRawDataType[] = [];
  for (const dataType of dataTypes.docs) {
    const parsedDataType = TRawDataType.safeParse({
      id: dataType.id,
      ...dataType.data(),
    });
    if (parsedDataType.success) {
      result.push(parsedDataType.data);
    }
  }
  return result;
};

export const usePropertyDataTypes = (propertyID: string) => {
  const $dataTypes = useQuery(usePropertyDataTypesKey(propertyID), () => fetchAllPropertyDataTypes(propertyID));

  return {
    $dataTypes,
    dataTypes: $dataTypes.data,
  };
};

const fetchGlobalDataType = async (dataTypeID: string) => {
  const dataType = await getDoc(doc(getFirestore(), "dataTypes", dataTypeID));
  return TRawDataType.parse({
    id: dataType.id,
    ...dataType.data(),
  });
};

export const useGlobalDataType = (id: string) => {
  const $dataType = useQuery(useGlobalDataTypeKey(id), () => fetchGlobalDataType(id));
  return {
    $dataType,
    dataType: $dataType.data,
  };
};

const fetchAllGlobalDataTypes = async (user: IRawUser, properties: IApiProperty[]): Promise<IRawDataType[]> => {
  const companies: Set<string> = new Set(user.companies ?? []);
  for (const property of properties) {
    for (const company of property.companies ?? []) {
      companies.add(company);
    }
  }

  const promises: Promise<IRawDataType[]>[] = [];
  for (const companyID of companies) {
    promises.push(
      getDocs(query(collection(getFirestore(), "dataTypes"), where("companyID", "==", companyID))).then((dataTypes) => {
        const result: IRawDataType[] = [];
        for (const dataType of dataTypes.docs) {
          const parsedDataType = TRawDataType.safeParse({
            id: dataType.id,
            ...dataType.data(),
          });
          if (parsedDataType.success) {
            result.push(parsedDataType.data);
          }
        }
        return result;
      })
    );
  }

  return Promise.all(promises).then((dataTypes) => dataTypes.flat());
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const prefetchAllGlobalDataTypes = async (userID: string) => {
  const user = queryClient.getQueryData<IRawUser>(useUserByIdKey(userID));
  const properties = queryClient.getQueryData<IApiProperty[]>(useAllPropertiesKey());
  if (!user || !properties) return;
  const queryKey = useGlobalDataTypesKey();
  const allGlobalDataTypes = await fetchAllGlobalDataTypes(user, properties);
  await queryClient.setQueryData(queryKey, allGlobalDataTypes);
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const hasFetchedGlobalDataTypes = () => {
  const queryKey = useGlobalDataTypesKey();
  const data = queryClient.getQueryData<IRawDataType[]>(queryKey);
  return !!data;
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const prefetchGlobalDataType = async (id: string) => {
  const queryKey = useGlobalDataTypeKey(id);
  const dataType = await fetchGlobalDataType(id);
  await queryClient.setQueryData(queryKey, dataType);
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const prefetchPropertyDataType = async (propertyID: string, dataTypeID: string) => {
  const queryKey = usePropertyDataTypeKey(propertyID, dataTypeID);
  const dataType = await fetchPropertyDataType(propertyID, dataTypeID);
  await queryClient.setQueryData(queryKey, dataType);
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const prefetchAllPropertyDataTypes = async (propertyID: string) => {
  const queryKey = usePropertyDataTypesKey(propertyID);
  const dataTypes = await fetchAllPropertyDataTypes(propertyID);
  await queryClient.setQueryData(queryKey, dataTypes);
};

/**
 * @deprecated Remove once CustomFieldDataTypeHelper is using hooks instead of class functions
 */
export const hasFetchedPropertyDataTypes = (propertyID: string) => {
  const queryKey = usePropertyDataTypesKey(propertyID);
  const data = queryClient.getQueryData<IRawDataType[]>(queryKey);
  return !!data;
};
